import { Controller } from '@hotwired/stimulus';

// Quick uploads into the Library (camera roll, files, voice memos), plus
// one-tap in-browser audio/video recording. Both paths feed the same upload
// queue, which POSTs each file to /library — the exact endpoint the Library
// page's own upload box uses. Saved recordings can be made public/featured
// straight from the done screen (same endpoints as the Library's chips).
export default class extends Controller {
  static targets = [
    'home',
    'recordSection',
    'fileInput',
    'live',
    'previewWrap',
    'preview',
    'flipButton',
    'flipLabel',
    'audioVisual',
    'timer',
    'saving',
    'progressBar',
    'progressText',
    'done',
    'doneName',
    'doneAssets',
    'error',
    'errorText',
    'retryButton',
  ];
  static values = {
    uploadUrl: String,
    visibilityUrl: String,
    featureUrl: String,
    ondemandUrl: String,
  };

  // Ordered by preference: mp4/m4a first because it plays everywhere and
  // matches the content types the Library already handles best; webm/ogg as
  // fallbacks for browsers that can't record mp4 (e.g. Firefox).
  static AUDIO_MIME_CANDIDATES = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  static VIDEO_MIME_CANDIDATES = [
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];

  static EXTENSIONS = {
    'audio/mp4': 'm4a',
    'audio/webm': 'weba',
    'audio/ogg': 'ogg',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
  };

  static THREE_GIGABYTES = 3221225472;

  // Video is for quick messages; audio (class recordings, voice notes) can
  // run long. The cap is enforced by the recording timer.
  static VIDEO_MAX_SECONDS = 30;

  connect() {
    this.mediaRecorder = null;
    this.stream = null;
    this.chunks = [];
    this.discarded = false;
    this.pendingUploads = [];
    this.savedAssets = [];
    this.timerInterval = null;
    this.wakeLock = null;
    this.facing = 'user';
    this.flipping = false;
    this.drawFrameId = null;
    this.canvasTrack = null;
    this.audioContext = null;

    // Uploading works everywhere; only hide the in-browser recording
    // buttons when MediaRecorder isn't available.
    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      !window.MediaRecorder
    ) {
      this.recordSectionTarget.hidden = true;
    }
  }

  disconnect() {
    this.stopTimer();
    this.releaseStream();
    this.releaseWakeLock();
  }

  // --- Upload from camera roll / files / voice memos ---

  pickFiles() {
    this.fileInputTarget.click();
  }

  filesSelected(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-picking the same file later

    if (!files.length) return;

    const items = [];
    const skipped = [];

    files.forEach(file => {
      const assetType = this.detectAssetType(file);
      if (!assetType) {
        skipped.push(
          `${file.name}: unsupported file type. Upload audio, video, document, or image files.`
        );
        return;
      }
      if (file.size > this.constructor.THREE_GIGABYTES) {
        skipped.push(`${file.name}: larger than the 3 GB limit.`);
        return;
      }
      items.push({ file, assetType });
    });

    if (!items.length) {
      this.pendingUploads = [];
      this.showError(skipped.join('\n') || 'No files were selected.');
      return;
    }

    this.skippedNotes = skipped;
    this.pendingUploads = items;
    this.startUploadQueue();
  }

  // --- Upload queue (shared by picked files and in-browser recordings) ---

  startUploadQueue() {
    if (!this.pendingUploads.length) return;

    this.failedUploads = [];
    this.savedAssets = [];
    this.showScreen('saving');
    this.uploadNext(0);
  }

  uploadNext(index) {
    if (index >= this.pendingUploads.length) {
      this.finishQueue();
      return;
    }

    this.uploadOne(this.pendingUploads[index], index, () =>
      this.uploadNext(index + 1)
    );
  }

  uploadOne(item, index, next) {
    const total = this.pendingUploads.length;
    const label = item.name || item.file.name;
    const size = this.humanSize(item.file.size);
    const position = total > 1 ? ` (${index + 1} of ${total})` : '';

    const overallPercent = filePercent =>
      Math.min(99, Math.round(((index + filePercent) * 100) / total));

    this.setProgress(
      overallPercent(0),
      `Uploading ${label} (${size})${position}…`
    );

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        const filePercent = event.loaded / event.total;
        this.setProgress(
          overallPercent(filePercent),
          `Uploading ${label} (${size})${position} — ${Math.round(
            filePercent * 100
          )}%`
        );
      }
    });

    // The bytes have all been sent; the server is now writing the file to
    // storage. Without this state a big file looks "hung" at 99%.
    xhr.upload.addEventListener('load', () => {
      this.setProgress(
        overallPercent(1),
        `Upload complete — filing ${label} in your Library…`
      );
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 400) {
        let payload = null;
        try {
          payload = JSON.parse(xhr.response);
        } catch (e) {
          // Older create responses had no body; the toggles just won't show.
        }
        this.savedAssets.push({
          id: payload && payload.id,
          label,
          assetType: item.assetType,
        });
      } else {
        this.failedUploads.push({
          item,
          message: `${label}: ${this.uploadErrorMessage(xhr)}`,
        });
      }
      next();
    });

    xhr.addEventListener('error', () => {
      this.failedUploads.push({
        item,
        message: `${label}: network error while saving.`,
      });
      next();
    });

    xhr.open('POST', this.uploadUrlValue, true);

    const data = new FormData();
    data.append('file', item.file);
    data.append('asset_type', item.assetType);
    if (item.name) data.append('name', item.name);

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken.content);
    }

    xhr.send(data);
  }

  finishQueue() {
    const failures = this.failedUploads;
    const skipped = this.skippedNotes || [];
    this.skippedNotes = [];

    if (!failures.length) {
      this.setProgress(100, 'Done!');
      this.pendingUploads = [];

      let summary =
        this.savedAssets.length === 1
          ? this.savedAssets[0].label
          : `${this.savedAssets.length} files saved.`;
      if (skipped.length) {
        summary += `\nSkipped: ${skipped.join(' ')}`;
      }
      this.doneNameTarget.textContent = summary;
      this.renderDoneAssets();
      this.showScreen('done');
    } else {
      // Keep only the failed items queued so "Try Saving Again" doesn't
      // re-upload what already succeeded.
      this.pendingUploads = failures.map(failure => failure.item);

      const lines = [];
      if (this.savedAssets.length > 0) {
        lines.push(
          `${this.savedAssets.length} saved, ${failures.length} didn't make it:`
        );
      }
      lines.push(...failures.map(failure => failure.message));
      lines.push(...skipped);
      this.showError(lines.join('\n'), { retryable: true });
    }
  }

  retryUpload() {
    this.startUploadQueue();
  }

  reset() {
    this.pendingUploads = [];
    this.savedAssets = [];
    this.doneAssetsTarget.innerHTML = '';
    this.showScreen('home');
  }

  // --- Done-screen public/feature toggles for saved recordings ---

  renderDoneAssets() {
    this.doneAssetsTarget.innerHTML = '';
    this.featureButtons = [];

    const recordings = this.savedAssets.filter(
      asset => asset.id && asset.assetType === 'recording'
    );
    if (!recordings.length) return;

    const note = document.createElement('p');
    note.className = 'rec-done-assets__note';
    note.textContent =
      'Recordings start private. Show one in the student On-Demand library, or make it public to embed it on your website — public recordings can also be featured on your studio page.';
    this.doneAssetsTarget.appendChild(note);

    recordings.forEach(asset => {
      this.doneAssetsTarget.appendChild(this.buildAssetRow(asset));
    });
  }

  buildAssetRow(asset) {
    const row = document.createElement('div');
    row.className = 'rec-asset-row';

    // Only show the name when several files were saved; for a single save
    // the summary line above the chips already names it.
    if (this.savedAssets.length > 1) {
      const name = document.createElement('div');
      name.className = 'rec-asset-row__name';
      name.textContent = asset.label;
      row.appendChild(name);
    }

    const chips = document.createElement('div');
    chips.className = 'rec-asset-row__chips';

    const onDemandButton = document.createElement('button');
    onDemandButton.type = 'button';
    onDemandButton.className = 'rec-chip rec-chip--ondemand';
    onDemandButton.textContent = 'Show in On-Demand';

    const publicButton = document.createElement('button');
    publicButton.type = 'button';
    publicButton.className = 'rec-chip';
    publicButton.textContent = 'Make Public';

    const featureButton = document.createElement('button');
    featureButton.type = 'button';
    featureButton.className = 'rec-chip rec-chip--feature';
    featureButton.textContent = 'Feature on Studio Page';
    featureButton.hidden = true;
    this.featureButtons.push(featureButton);

    const setPublicState = isPublic => {
      publicButton.classList.toggle('is-on', isPublic);
      publicButton.textContent = isPublic ? 'Public ✓' : 'Make Public';
      featureButton.hidden = !isPublic;
    };

    const setOnDemandState = isOn => {
      onDemandButton.classList.toggle('is-on', isOn);
      onDemandButton.textContent = isOn
        ? 'In On-Demand ✓'
        : 'Show in On-Demand';
    };

    const setFeaturedState = isFeatured => {
      featureButton.classList.toggle('is-on', isFeatured);
      featureButton.textContent = isFeatured
        ? 'Featured ★'
        : 'Feature on Studio Page';
    };

    publicButton.addEventListener('click', () => {
      const makePublic = !publicButton.classList.contains('is-on');
      this.toggleChip(publicButton, () =>
        this.patchJson(this.assetUrl(this.visibilityUrlValue, asset.id), {
          public: makePublic,
        }).then(state => {
          // Ladder: public also adds to On-Demand; private removes it.
          setPublicState(state.public);
          setFeaturedState(state.featured);
          setOnDemandState(state.on_demand);
        })
      );
    });

    onDemandButton.addEventListener('click', () => {
      const makeAvailable = !onDemandButton.classList.contains('is-on');
      this.toggleChip(onDemandButton, () =>
        this.patchJson(this.assetUrl(this.ondemandUrlValue, asset.id), {
          on_demand: makeAvailable,
        }).then(state => {
          setOnDemandState(state.on_demand);
        })
      );
    });

    featureButton.addEventListener('click', () => {
      const makeFeatured = !featureButton.classList.contains('is-on');
      this.toggleChip(featureButton, () =>
        this.patchJson(this.assetUrl(this.featureUrlValue, asset.id), {
          featured: makeFeatured,
        }).then(state => {
          // One featured recording per studio: featuring this one clears
          // the flag everywhere else, including other rows here.
          if (state.featured) {
            this.featureButtons.forEach(button => {
              if (button !== featureButton) {
                button.classList.remove('is-on');
                button.textContent = 'Feature on Studio Page';
              }
            });
          }
          setFeaturedState(state.featured);
        })
      );
    });

    chips.appendChild(onDemandButton);
    chips.appendChild(publicButton);
    chips.appendChild(featureButton);
    row.appendChild(chips);
    return row;
  }

  toggleChip(button, request) {
    if (button.disabled) return;
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Saving…';

    request()
      .catch(() => {
        button.textContent = originalText;
      })
      .finally(() => {
        button.disabled = false;
        if (button.textContent === 'Saving…') button.textContent = originalText;
      });
  }

  assetUrl(template, id) {
    return template.replace('ASSET_ID', id);
  }

  patchJson(url, body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken ? csrfToken.content : '',
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(response => {
      if (!response.ok) throw new Error('Request failed');
      return response.json();
    });
  }

  // --- In-browser recording ---

  startAudio() {
    this.start('audio');
  }

  startVideo() {
    this.start('video');
  }

  async start(mode) {
    if (this.mediaRecorder) return; // already recording

    this.mode = mode;
    this.facing = 'user';

    if (mode === 'audio') {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        this.showPermissionError(mode, error);
        return;
      }
      this.stream = stream;
      this.previewWrapTarget.hidden = true;
      this.audioVisualTarget.hidden = false;
      this.beginRecording(stream);
      return;
    }

    await this.startVideoRecording();
  }

  // Video records a CANVAS stream, not the camera track directly: the camera
  // is painted onto the canvas every frame, and the mic is routed through a
  // WebAudio destination node. Both recorded tracks therefore never change
  // identity, which is what makes flipping cameras mid-take possible — on
  // iOS Safari the only way to switch cameras is a brand-new getUserMedia,
  // and swapping tracks on a directly-recorded stream kills MediaRecorder.
  async startVideoRecording() {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facing },
        audio: true,
      });
    } catch (error) {
      this.showPermissionError('video', error);
      return;
    }

    this.previewWrapTarget.hidden = false;
    this.audioVisualTarget.hidden = true;
    this.attachCamera(stream, this.facing);

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const canFlip =
      typeof document.createElement('canvas').captureStream === 'function' &&
      !!AudioContextClass;

    if (!canFlip) {
      // Very old browsers: record the camera directly; no flipping.
      this.flipButtonTarget.hidden = true;
      this.beginRecording(stream);
      return;
    }

    try {
      this.audioContext = new AudioContextClass();
      if (this.audioContext.resume) this.audioContext.resume().catch(() => {});
      this.audioDestination = this.audioContext.createMediaStreamDestination();
      this.connectMic(stream);

      await this.waitForPreviewDimensions();
      this.canvas = document.createElement('canvas');
      this.canvasContext = this.canvas.getContext('2d');
      this.sizeCanvas();

      let canvasStream;
      try {
        canvasStream = this.canvas.captureStream(30);
      } catch (e) {
        canvasStream = this.canvas.captureStream();
      }
      this.canvasTrack = canvasStream.getVideoTracks()[0];

      this.startDrawLoop();

      const recordingStream = new MediaStream([
        this.canvasTrack,
        this.audioDestination.stream.getAudioTracks()[0],
      ]);

      if (this.beginRecording(recordingStream)) {
        this.flipButtonTarget.hidden = false;
        this.hideFlipWithoutSecondCamera();
      }
    } catch (error) {
      // Compositing failed (e.g. canvas quirk) — fall back to recording the
      // camera directly. Everything works except mid-take flipping.
      this.teardownVideoPipeline();
      this.flipButtonTarget.hidden = true;
      this.beginRecording(stream);
    }
  }

  beginRecording(recordingStream) {
    const mimeType = this.pickMimeType(this.mode);
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(recordingStream, { mimeType })
        : new MediaRecorder(recordingStream);
    } catch (error) {
      this.releaseStream();
      this.showError(`Couldn't start the recorder: ${error.message}`);
      return false;
    }

    this.mediaRecorder = recorder;
    this.chunks = [];
    this.discarded = false;

    recorder.addEventListener('dataavailable', event => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    });

    recorder.addEventListener('stop', () => this.recordingStopped());

    recorder.start(1000); // gather data every second so long takes stay safe

    this.showScreen('live');
    this.startTimer(this.mode);
    this.requestWakeLock();
    return true;
  }

  attachCamera(stream, facing) {
    this.stream = stream;
    this.facing = facing;
    this.previewTarget.srcObject = stream;
    const played = this.previewTarget.play && this.previewTarget.play();
    if (played && played.catch) played.catch(() => {});
    this.updatePreviewMirror();
  }

  // Route the mic through the shared WebAudio destination. Reconnected on
  // every flip because iOS can end the previous stream's audio track when a
  // new getUserMedia call is made.
  connectMic(stream) {
    if (!this.audioContext || !this.audioDestination) return;
    const track = stream.getAudioTracks()[0];
    if (!track) return;

    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch (e) {
        // already disconnected
      }
    }
    this.micSource = this.audioContext.createMediaStreamSource(
      new MediaStream([track])
    );
    this.micSource.connect(this.audioDestination);
  }

  waitForPreviewDimensions() {
    const video = this.previewTarget;
    if (video.videoWidth > 0) return Promise.resolve();
    return new Promise(resolve => {
      const done = () => {
        video.removeEventListener('loadedmetadata', done);
        resolve();
      };
      video.addEventListener('loadedmetadata', done);
      setTimeout(done, 3000); // don't hang forever; sizeCanvas has defaults
    });
  }

  sizeCanvas() {
    let width = this.previewTarget.videoWidth || 720;
    let height = this.previewTarget.videoHeight || 1280;
    const MAX_EDGE = 1280;
    const longest = Math.max(width, height);
    if (longest > MAX_EDGE) {
      const scale = MAX_EDGE / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    this.canvas.width = width;
    this.canvas.height = height;
  }

  startDrawLoop() {
    const draw = () => {
      const video = this.previewTarget;
      // Cover-fit so a flip to a camera with a different aspect ratio never
      // distorts the recording; during the brief camera swap the canvas
      // simply holds the last painted frame.
      if (video.readyState >= 2 && video.videoWidth > 0) {
        const cw = this.canvas.width;
        const ch = this.canvas.height;
        const scale = Math.max(cw / video.videoWidth, ch / video.videoHeight);
        const dw = video.videoWidth * scale;
        const dh = video.videoHeight * scale;
        this.canvasContext.drawImage(
          video,
          (cw - dw) / 2,
          (ch - dh) / 2,
          dw,
          dh
        );
      }
      this.drawFrameId = requestAnimationFrame(draw);
    };
    this.drawFrameId = requestAnimationFrame(draw);
  }

  // Flip = acquire the other camera with a fresh getUserMedia and repoint the
  // preview (which the draw loop paints from). The recorded canvas/audio
  // tracks never change, so MediaRecorder keeps rolling through the switch.
  async flipCamera() {
    if (this.mode !== 'video' || !this.mediaRecorder || this.flipping) return;
    if (!this.canvasTrack) {
      this.showFlipUnavailable();
      return;
    }

    this.flipping = true;
    const previous = this.facing;
    const next = previous === 'user' ? 'environment' : 'user';

    // Platforms that allow two open cameras (desktop, most Android) can
    // acquire the new one before releasing the old for a seamless swap.
    let newStream = await this.acquireCamera(next);

    if (!newStream) {
      // iOS allows one active camera: release ours, then try again.
      this.stopStreamTracks(this.stream);
      newStream = await this.acquireCamera(next);
    }

    if (newStream) {
      if (this.stream && this.stream !== newStream) {
        this.stopStreamTracks(this.stream);
      }
      this.attachCamera(newStream, next);
      this.connectMic(newStream);
    } else {
      // Couldn't get the other camera — reacquire the one we had so the
      // recording keeps its picture and sound.
      const restored = await this.acquireCamera(previous);
      if (restored) {
        this.attachCamera(restored, previous);
        this.connectMic(restored);
      }
      this.showFlipUnavailable();
    }

    this.flipping = false;
  }

  async acquireCamera(facing) {
    const attempts = [
      { video: { facingMode: { exact: facing } }, audio: true },
      { video: { facingMode: facing }, audio: true },
    ];

    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        const settings =
          (stream.getVideoTracks()[0] &&
            stream.getVideoTracks()[0].getSettings &&
            stream.getVideoTracks()[0].getSettings()) ||
          {};
        // A non-exact request can hand back the camera we already had;
        // treat that as a miss (when the device reports facing at all).
        if (
          constraints.video.facingMode.exact ||
          !settings.facingMode ||
          settings.facingMode === facing
        ) {
          return stream;
        }
        this.stopStreamTracks(stream);
      } catch (error) {
        // try the next, looser constraint
      }
    }
    return null;
  }

  // Flipping needs somewhere to flip to.
  hideFlipWithoutSecondCamera() {
    if (!navigator.mediaDevices.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        const cameras = devices.filter(d => d.kind === 'videoinput');
        if (cameras.length < 2) this.flipButtonTarget.hidden = true;
      })
      .catch(() => {});
  }

  showFlipUnavailable() {
    const label = this.flipLabelTarget;
    const original = 'Flip';
    label.textContent = 'Unavailable';
    setTimeout(() => {
      label.textContent = original;
    }, 1800);
  }

  // Mirror the self-view for the front camera (like the native camera app).
  // The recorded video itself is never mirrored.
  updatePreviewMirror() {
    this.previewTarget.classList.toggle(
      'rec-preview--mirrored',
      this.facing === 'user'
    );
  }

  stop() {
    if (!this.mediaRecorder) return;
    this.discarded = false;
    this.mediaRecorder.stop();
  }

  discard() {
    if (!this.mediaRecorder) return;
    this.discarded = true;
    this.mediaRecorder.stop();
  }

  recordingStopped() {
    this.stopTimer();
    this.releaseStream();
    this.releaseWakeLock();

    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;

    if (this.discarded) {
      this.chunks = [];
      this.showScreen('home');
      return;
    }

    const baseType = (recorder.mimeType || this.chunks[0]?.type || '').split(
      ';'
    )[0];
    const blob = new Blob(
      this.chunks,
      baseType ? { type: baseType } : undefined
    );
    this.chunks = [];

    if (blob.size === 0) {
      this.showError('Nothing was recorded. Please try again.');
      return;
    }

    // Queue the recording; a failed upload can be retried without losing
    // the take.
    this.skippedNotes = [];
    this.pendingUploads = [this.buildRecordingItem(blob, baseType)];
    this.startUploadQueue();
  }

  buildRecordingItem(blob, baseType) {
    const type =
      baseType || (this.mode === 'video' ? 'video/webm' : 'audio/webm');
    const extension =
      this.constructor.EXTENSIONS[type] ||
      (this.mode === 'video' ? 'webm' : 'weba');

    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
      now.getDate()
    )}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const kind = this.mode === 'video' ? 'Video' : 'Audio';
    const displayName = `${kind} Recording — ${now.toLocaleDateString(
      undefined,
      { month: 'short', day: 'numeric', year: 'numeric' }
    )}, ${now.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;

    return {
      file: new File(
        [blob],
        `${kind.toLowerCase()}-recording-${stamp}.${extension}`,
        { type }
      ),
      name: displayName,
      assetType: 'recording',
    };
  }

  // --- Helpers ---

  // Mirrors LibraryAsset's content-type lists (and the Library page's own
  // upload box) so nothing is sent that the server would reject.
  detectAssetType(file) {
    const recordingTypes = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wave',
      'audio/wav',
      'audio/x-wav',
      'audio/x-pn-wav',
      'audio/vnd.wav',
      'audio/ogg',
      'audio/flac',
      'audio/x-flac',
      'audio/x-caf',
      'audio/webm',
      'audio/m4a',
      'audio/x-m4a',
      'audio/mp4',
      'audio/aac',
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-m4v',
    ];

    const documentTypes = [
      'application/vnd.apple.pages',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/pdf',
      'text/plain',
    ];

    const contentType = file.type;

    if (recordingTypes.includes(contentType)) {
      return 'recording';
    } else if (documentTypes.includes(contentType)) {
      return 'document';
    } else if (contentType && contentType.startsWith('image/')) {
      return 'image';
    }

    // Some browsers/OSes report no MIME type for less common files;
    // fall back to the extension.
    return this.detectByExtension(file.name);
  }

  detectByExtension(fileName) {
    const ext = (fileName.split('.').pop() || '').toLowerCase();

    const recordingExts = [
      'mp3',
      'wav',
      'ogg',
      'flac',
      'm4a',
      'caf',
      'mp4',
      'webm',
      'weba',
      'mov',
      'm4v',
      'aac',
    ];
    const documentExts = ['pdf', 'doc', 'docx', 'pages', 'txt'];
    const imageExts = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'bmp',
      'heic',
      'tif',
      'tiff',
    ];

    if (recordingExts.includes(ext)) return 'recording';
    if (documentExts.includes(ext)) return 'document';
    if (imageExts.includes(ext)) return 'image';
    return null;
  }

  humanSize(bytes) {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1073741824) return `${(size / 1048576).toFixed(1)} MB`;
    return `${(size / 1073741824).toFixed(2)} GB`;
  }

  pickMimeType(mode) {
    const candidates =
      mode === 'video'
        ? this.constructor.VIDEO_MIME_CANDIDATES
        : this.constructor.AUDIO_MIME_CANDIDATES;

    if (!MediaRecorder.isTypeSupported) return null;
    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || null;
  }

  showScreen(name) {
    this.homeTarget.hidden = name !== 'home';
    this.liveTarget.hidden = name !== 'live';
    this.savingTarget.hidden = name !== 'saving';
    this.doneTarget.hidden = name !== 'done';
    this.errorTarget.hidden = name !== 'error';
  }

  showError(message, { retryable = false } = {}) {
    this.errorTextTarget.textContent = message;
    this.retryButtonTarget.hidden = !(
      retryable && this.pendingUploads.length > 0
    );
    this.showScreen('error');
  }

  showPermissionError(mode, error) {
    const device = mode === 'video' ? 'camera and microphone' : 'microphone';
    if (
      error &&
      (error.name === 'NotAllowedError' || error.name === 'SecurityError')
    ) {
      this.showError(
        `Please allow access to your ${device}, then try again. You can enable it in your browser or device settings.`
      );
    } else if (error && error.name === 'NotFoundError') {
      this.showError(`No ${device} was found on this device.`);
    } else {
      this.showError(
        `Couldn't access your ${device}: ${
          error ? error.message : 'unknown error'
        }`
      );
    }
  }

  uploadErrorMessage(xhr) {
    if (xhr.status === 422) {
      try {
        const errors = JSON.parse(xhr.response);
        return Object.values(errors)
          .flat()
          .join(', ');
      } catch (e) {
        return 'the file was rejected.';
      }
    }
    return `the server responded with an error (${xhr.status}).`;
  }

  setProgress(percent, text) {
    this.progressBarTarget.style.width = `${percent}%`;
    this.progressTextTarget.textContent = text;
  }

  startTimer(mode) {
    this.recordingStartedAt = Date.now();
    const maxSeconds =
      mode === 'video' ? this.constructor.VIDEO_MAX_SECONDS : null;
    const pad = n => String(n).padStart(2, '0');
    const format = total => {
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
    };

    this.timerTarget.textContent = maxSeconds
      ? `0:00 / ${format(maxSeconds)}`
      : '0:00';

    this.timerInterval = setInterval(() => {
      const total = Math.floor((Date.now() - this.recordingStartedAt) / 1000);

      if (maxSeconds) {
        this.timerTarget.textContent = `${format(
          Math.min(total, maxSeconds)
        )} / ${format(maxSeconds)}`;
        // Quick video messages only — auto stop & save at the cap.
        if (total >= maxSeconds) this.stop();
      } else {
        this.timerTarget.textContent = format(total);
      }
    }, 250);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  releaseStream() {
    this.teardownVideoPipeline();
    this.stopStreamTracks(this.stream);
    this.stream = null;
    if (this.hasPreviewTarget) {
      this.previewTarget.srcObject = null;
    }
  }

  stopStreamTracks(stream) {
    if (!stream) return;
    stream.getTracks().forEach(track => track.stop());
  }

  teardownVideoPipeline() {
    if (this.drawFrameId) {
      cancelAnimationFrame(this.drawFrameId);
      this.drawFrameId = null;
    }
    if (this.canvasTrack) {
      this.canvasTrack.stop();
      this.canvasTrack = null;
    }
    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch (e) {
        // already disconnected
      }
      this.micSource = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
      this.audioDestination = null;
    }
    this.canvas = null;
    this.canvasContext = null;
  }

  // Keep the screen awake during a take — the OS putting the display to
  // sleep can suspend capture on mobile devices.
  async requestWakeLock() {
    try {
      if ('wakeLock' in navigator) {
        this.wakeLock = await navigator.wakeLock.request('screen');
      }
    } catch (e) {
      // Not critical; recording still works while the screen is on.
    }
  }

  releaseWakeLock() {
    if (this.wakeLock) {
      this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
    }
  }
}
